import {
    Component,
    computed,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Textarea } from 'primeng/textarea';
import { DisplayInfo, FieldUpdate, GameVariant } from '@/lib-backend';
import * as game from '@/lib-backend/methods/game';
import { RealmGameVariantsService } from '@/app/services/realm-game-variants.service';
import {
    modeConfigTypeLabel,
    variantEntryFee,
    variantPlayerCount,
    variantTopReward,
} from '@/app/utils/variant';
import { FundsDisplay } from '@/app/components/funds-display';

interface EditDraft {
    overrideDisplayInfo: boolean;
    displayName: string;
    displayDescription: string;
    displayImageUrl: string;
    overrideIsActive: boolean;
    isActive: boolean;
}

@Component({
    selector: 'app-edit-variant-dialog',
    standalone: true,
    imports: [FormsModule, Button, Checkbox, Dialog, InputText, Message, Textarea, FundsDisplay],
    templateUrl: './edit-variant-dialog.html',
})
export class EditVariantDialog {
    @Input({ required: true }) set visible(v: boolean) {
        this.visibleSignal.set(v);
    }
    @Input({ required: true }) realmId = '';
    @Input({ required: true }) gameId = '';

    @Input() set adminVariant(v: GameVariant | null) {
        this.editingVariant.set(v);
    }

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() saved = new EventEmitter<void>();

    protected readonly variantsService = inject(RealmGameVariantsService);

    protected readonly visibleSignal = signal(false);
    protected readonly editingVariant = signal<GameVariant | null>(null);
    protected readonly draft = signal<EditDraft>(this.makeEmptyDraft());
    private readonly initialDraft = signal<EditDraft>(this.makeEmptyDraft());

    protected readonly saving = signal(false);
    protected readonly saveError = signal<string | null>(null);

    protected readonly canSave = computed(() => {
        const d = this.draft();
        if (d.overrideDisplayInfo && !d.displayName.trim()) return false;
        return JSON.stringify(d) !== JSON.stringify(this.initialDraft());
    });

    protected readonly modeConfigTypeLabel = modeConfigTypeLabel;
    protected readonly variantPlayerCount = variantPlayerCount;
    protected readonly variantEntryFee = variantEntryFee;
    protected readonly variantTopReward = variantTopReward;

    constructor() {
        effect(() => {
            if (this.visibleSignal()) {
                const v = this.editingVariant();
                if (v) {
                    const initial = this.variantToDraft(v);
                    this.draft.set(structuredClone(initial));
                    this.initialDraft.set(structuredClone(initial));
                    this.saveError.set(null);
                }
            }
        });
    }

    protected setField<K extends keyof EditDraft>(key: K, value: EditDraft[K]): void {
        this.draft.update((d) => ({ ...d, [key]: value }));
    }

    protected toggleDisplayOverride(value: boolean): void {
        const v = this.editingVariant();
        if (!v) return;
        this.draft.update((d) => {
            if (value && !d.overrideDisplayInfo) {
                const di = v.DisplayInfo ?? v.Template.DisplayInfo;
                return {
                    ...d,
                    overrideDisplayInfo: true,
                    displayName: di.Name,
                    displayDescription: di.Description,
                    displayImageUrl: di.ImageUrl,
                };
            }
            return { ...d, overrideDisplayInfo: value };
        });
    }

    protected toggleActiveOverride(value: boolean): void {
        const v = this.editingVariant();
        if (!v) return;
        this.draft.update((d) => {
            if (value && !d.overrideIsActive) {
                return {
                    ...d,
                    overrideIsActive: true,
                    isActive: v.IsActive ?? v.Template.IsActive,
                };
            }
            return { ...d, overrideIsActive: value };
        });
    }

    protected onVisibleChange(value: boolean): void {
        this.visibleSignal.set(value);
        this.visibleChange.emit(value);
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    protected templateActiveLabel(): string {
        const v = this.editingVariant();
        return v?.Template.IsActive ? 'Active' : 'Inactive';
    }

    protected async save(): Promise<void> {
        const v = this.editingVariant();
        if (!v || !this.canSave()) return;
        this.saving.set(true);
        this.saveError.set(null);
        try {
            const params: game.UpdateGameVariantRequest = {
                RealmId: this.realmId,
                GameId: this.gameId,
                GameVariantId: v.GameVariantId,
                IsActive: this.diffIsActive(v),
                DisplayInfo: this.diffDisplayInfo(v),
            };
            const result = await this.variantsService.updateVariant(params);
            if (result.kind === 'ok') {
                this.saved.emit();
                this.close();
            } else {
                this.saveError.set(this.formatError(result.reason));
            }
        } finally {
            this.saving.set(false);
        }
    }

    private diffIsActive(v: GameVariant): FieldUpdate<boolean | null> {
        const d = this.draft();
        const original = v.IsActive;
        if (!d.overrideIsActive) {
            return original !== null ? { value: null } : null;
        }
        if (original === d.isActive) return null;
        return { value: d.isActive };
    }

    private diffDisplayInfo(v: GameVariant): FieldUpdate<DisplayInfo | null> {
        const d = this.draft();
        const original = v.DisplayInfo;
        if (!d.overrideDisplayInfo) {
            return original !== null ? { value: null } : null;
        }
        const newDisplay: DisplayInfo = {
            Name: d.displayName,
            Description: d.displayDescription,
            ImageUrl: d.displayImageUrl,
        };
        if (original !== null && JSON.stringify(original) === JSON.stringify(newDisplay)) {
            return null;
        }
        return { value: newDisplay };
    }

    private variantToDraft(v: GameVariant): EditDraft {
        const di = v.DisplayInfo ?? v.Template.DisplayInfo;
        return {
            overrideDisplayInfo: v.DisplayInfo !== null,
            displayName: di.Name,
            displayDescription: di.Description,
            displayImageUrl: di.ImageUrl,
            overrideIsActive: v.IsActive !== null,
            isActive: v.IsActive ?? v.Template.IsActive,
        };
    }

    private makeEmptyDraft(): EditDraft {
        return {
            overrideDisplayInfo: false,
            displayName: '',
            displayDescription: '',
            displayImageUrl: '',
            overrideIsActive: false,
            isActive: false,
        };
    }

    private formatError(reason: string): string {
        switch (reason) {
            case 'GameNotFound': return 'Game not found.';
            case 'GameVariantNotFound': return 'Variant not found on the game server.';
            default: return 'Save failed. Please try again.';
        }
    }
}
